// CreateStackButton.js

import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Box,
  ButtonBase,
} from '@mui/material';
import { 
  ListAlt as ListAltIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { buttonStyles } from './common/ButtonStyles';
import { AutoAwesomeIcon } from '../icons';
import { alpha } from '@mui/material/styles';
import { color, radius, r } from '../theme/tokens';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import {
  AI_MENU_ITEM_TITLE,
  AI_MENU_ITEM_DESCRIPTION,
  MENU_ITEM_TITLE,
  MENU_ITEM_DESCRIPTION,
} from './analyze/copy';

/** Where a lapsed member's New stack CTA leads instead of the menu. */
const SUBSCRIPTION_ROUTE = '/profile?section=account-subscription';

/** Where "Create with myStack AI" leads. */
export const CREATE_ROUTE = '/create-stack';

/**
 * Where "Analyze Current Stack" leads.
 *
 * The route is unchanged from before the entry point was retired (ruled: it
 * stays `/manual-create-stack`), even though the page behind it is now the
 * analysis form rather than a plain manual editor. Renaming it would break
 * every bookmark for no reader-visible gain.
 */
export const ANALYZE_ROUTE = '/manual-create-stack';

/**
 * Where the shared menu opens, per trigger (Tanu, 2026-08-29).
 *
 * - **Dashed card:** beside it — the menu's LEFT edge against the card's RIGHT
 *   edge, tops aligned — so it opens into the empty grid space rather than
 *   pushing down the page.
 * - **Header button:** unchanged — below the button, right edges aligned.
 *
 * A pure function, and exported, because it is the only honest way to assert
 * this. MUI resolves an origin against the paper's measured box, and in jsdom
 * that box is 0×0, so both placements come out of the DOM as the same
 * `transform-origin` and a rendered test would pass whatever this returned.
 *
 * The header half matters as much as the card half: the two triggers share one
 * `Menu` deliberately ("two copies of that menu would drift"), so a placement
 * aimed at the card moves the header button's menu too unless it is made
 * conditional here.
 */
export function menuPlacement(asCard) {
  return asCard
    ? {
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
      }
    : {
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        transformOrigin: { horizontal: 'right', vertical: 'top' },
      };
}

/**
 * The New stack trigger: the header button, or with `asCard` the dashed cell
 * that is always the last cell of the My stacks grid (render 4b; ruled by Tanu
 * on 2026-08-26 so the entry point never leaves the grid). Both triggers open
 * the same menu, which is why this is a prop and not a second component: two
 * copies of that menu would drift.
 *
 * **The two-item menu is back** (STACK-544). It was retired on 2026-09-12
 * because the manual-creation feature it led to was being redesigned, and a
 * one-item menu is a click that chooses nothing. The redesign is this ticket:
 * `/manual-create-stack` now serves Analyze Current Stack, so there are two
 * destinations again and the menu is the thing that chooses between them.
 * STACK-527's placement rule applies again with it — see `menuPlacement`.
 *
 * **For a lapsed member both triggers go to reactivation instead of opening
 * the menu** (Tanu, 2026-08-28). The CTA stays — it is the ask, not the
 * obstacle — but it now leads where the membership has to be renewed first.
 *
 * Two things to be honest about, because the code cannot say them itself:
 *
 * - **This is a redirect, not a gate.** `/create-stack` and
 *   `/manual-create-stack` are still routable directly, and the generation
 *   endpoints (`/api/generate-stack`, `/api/generate-research-stack`,
 *   `/api/generate-research-stack-parallel`) are `@optional_authentication`
 *   with no `@pro_feature_required`. A bookmark or a back button still
 *   reaches them. The analysis endpoint `/api/v2/analyze-stack` is the one
 *   exception — it is in `PRO_API_ENDPOINTS` and carries
 *   `@pro_feature_required` (PR 345) — so a lapsed member who bookmarks the
 *   analyze route reaches the form but not a result. That asymmetry is the
 *   backend's, and this ticket does not change either side of it.
 * - **It is a product change, not a restored behaviour.** Stack creation is
 *   explicitly free today: `config.FEATURES.IS_PRO_FEATURE` marks
 *   `basic_stack_generation` and `save_stacks` as `false`, and `RequireAuth`
 *   authorises lapsed members deliberately ("they get basic functionality").
 *   Making creation a membership feature means flipping those flags and
 *   adding the backend decorator, which is a claims change with its own
 *   runbook — `.cursor/skills/subscription-claims/SKILL.md` — and is not done
 *   here.
 * - **The decision waits for membership to be known.** See `membershipUnknown`
 *   below. Deciding on first paint let a lapsed member through the window
 *   before the subscription-history read landed — caught by PR-Agent, not by
 *   the tests, which is why there is now one for it.
 */
function CreateStackButton({ asCard = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { isLapsed, checkingHistory } = useFeatureAccess();
  const membershipPaused = isLapsed();

  /**
   * **Do not decide while membership is still unknown.**
   *
   * `isLapsed()` answers `false` until the subscription-history read lands,
   * because `hasSubscriptionHistory` starts `false`. The dashboard's own
   * loading gate is a different read and does not cover this one — a sighted
   * pass measured roughly 0.4s where the page is interactive before membership
   * resolves. Deciding inside that window let a lapsed member click through to
   * the create menu, which is the whole behaviour this CTA exists to prevent.
   *
   * Held rather than guessed, which is what `RequireAuth` already does with
   * this same flag. Guessing the other way is worse than a brief disabled
   * control: treating "unknown" as lapsed would bounce a paying member to the
   * subscription page on their own first paint.
   */
  const membershipUnknown = checkingHistory;

  const openMenuOrReactivate = (event) => {
    if (membershipUnknown) return;
    if (membershipPaused) {
      navigate(SUBSCRIPTION_ROUTE);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  /**
   * Keyed off the trigger rather than a breakpoint, because the card is the
   * thing that has space beside it. The card does not render below `md`
   * (`MyStacksSection`), so "beside" is only ever asked for at a width that has
   * room — and MUI keeps a menu inside the viewport regardless, so a full grid
   * at 1440 shifts it rather than pushing it off-screen.
   */
  const { anchorOrigin: menuAnchorOrigin, transformOrigin: menuTransformOrigin } =
    menuPlacement(asCard);

  const trigger = asCard ? (
    <ButtonBase
      onClick={openMenuOrReactivate}
      disabled={membershipUnknown}
      aria-busy={membershipUnknown || undefined}
      aria-label={
        membershipPaused
          ? 'Reactivate your membership to create a stack'
          : 'Create new supplement stack'
      }
      aria-haspopup={membershipPaused ? undefined : 'menu'}
      aria-expanded={membershipPaused ? undefined : Boolean(anchorEl)}
      data-testid="new-stack-card"
      sx={{
        width: '100%',
        minHeight: '11rem',
        height: '100%',
        borderRadius: radius.lg,
        border: `${r(1)} dashed ${color.line}`,
        // `linkHover`, not `accent`: accent as text on paper is 3.44:1 and
        // fails AA. Same green, deep enough to read (Tanu, 2026-08-26).
        color: color.linkHover,
        fontSize: '0.9375rem',
        fontWeight: 700,
        '&:hover': {
          borderColor: color.accent,
          backgroundColor: alpha(color.accent, 0.04),
        },
        '&:focus-visible': {
          outline: `${r(2)} solid ${color.accent}`,
          outlineOffset: r(2),
        },
      }}
    >
      + New stack
    </ButtonBase>
  ) : (
      <Button
        variant="contained"
        onClick={openMenuOrReactivate}
        disabled={membershipUnknown}
        aria-busy={membershipUnknown || undefined}
        aria-label={
          membershipPaused
            ? 'Reactivate your membership to create a stack'
            : 'Create new supplement stack'
        }
        // Both attributes, on both triggers. The header button carried only
        // `aria-expanded` before the menu was retired, which tells a screen
        // reader that something expands without saying it is a menu. A lapsed
        // member's trigger claims neither, because for them it is a link to
        // the subscription page and announcing a popup would be a lie.
        aria-haspopup={membershipPaused ? undefined : 'menu'}
        aria-expanded={membershipPaused ? undefined : Boolean(anchorEl)}
        sx={{
          ...buttonStyles.primary,
          // New stack is a CTA, so it takes `accent` — the design system's
          // stated action colour (Tanu, 2026-08-26), and what renders 4a/4b/4e
          // draw. Overridden here rather than in `buttonStyles.primary`,
          // which SupplementsTable, StackCreationForm and AuthStyles also use
          // and which should stay evergreen for them.
          bgcolor: color.accent,
          color: color.accentInk,
          '&:hover': { bgcolor: color.accentHover },
          gap: r(6),
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={buttonStyles.iconWrapper}>
          <AddIcon sx={{ fontSize: '1.1rem' }} />
          <span>New Stack</span>
        </Box>
        <KeyboardArrowDownIcon sx={{ 
          fontSize: '1.2rem', 
          ml: r(2),
          opacity: 0.8
        }} />
      </Button>
  );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          elevation: 3,
          sx: {
            // `r(320)` = 20rem, not a bare 320 (which MUI resolves to px).
            // Restored verbatim from before the retirement, where it predated
            // the repo's rem rule; flagged on review and fixed here rather
            // than carried back in. The design-token guard's px-literal rule
            // does not catch it — a bare number in an `sx` prop is not a `px`
            // literal — so the rule is enforced by reading, which is why this
            // comment exists.
            width: r(320),
            borderRadius: radius.md,
            // The offset follows the placement: below the header button, beside
            // the card. See the origins below.
            //
            // `r(12)`, not `1.5`. MUI spacing shorthand resolves through
            // `theme.spacing`, and this project has **not** overridden the
            // default 8px base — `theme.spacing(1.5)` is `12px`. The frontend
            // CLAUDE.md claimed it was `0.5rem`; measured, and corrected there.
            ...(asCard ? { ml: r(12) } : { mt: r(12) }),
            padding: r(12),
            backgroundColor: color.surface,
            boxShadow: `0 ${r(4)} ${r(20)} rgba(0,0,0,0.15)`,
            animation: 'fadeIn 0.15s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: `translateY(${r(-8)})` },
              to: { opacity: 1, transform: 'translateY(0)' }
            },
            '& .MuiMenuItem-root': {
              borderRadius: r(10),
              mb: 1,
              backgroundColor: color.paper,
              border: `${r(1)} solid rgba(0,0,0,0.05)`,
              transition: 'all 0.2s ease',
              '&:hover': {
                // Was three hand-mixed seagreen rgba()s, two of which disagreed
                // on the red channel (46 vs 43). One accent token, three alphas.
                backgroundColor: alpha(color.accent, 0.04),
                transform: `translateY(${r(-1)})`,
                boxShadow: `0 ${r(2)} ${r(8)} ${alpha(color.accent, 0.12)}`,
                border: `${r(1)} solid ${alpha(color.accent, 0.2)}`
              },
              '&:active': {
                transform: 'scale(0.99)',
              },
              '&:last-child': {
                mb: 0
              }
            }
          },
        }}
        transformOrigin={menuTransformOrigin}
        anchorOrigin={menuAnchorOrigin}
        disableAutoFocusItem
      >
        <MenuItem 
          onClick={() => {
            navigate(CREATE_ROUTE);
            setAnchorEl(null);
          }}
          sx={{
            py: 2,
            whiteSpace: 'normal',
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1,
            width: '100%'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1,
              width: '100%'
            }}>
              <ListItemIcon sx={{
                color: color.evergreen,
                minWidth: r(36)
              }}>
                <AutoAwesomeIcon />
              </ListItemIcon>
              <Typography 
                variant="subtitle1"
                sx={{ 
                  fontWeight: 600,
                  color: color.ink,
                  flex: 1
                }}
              >
                {AI_MENU_ITEM_TITLE}
              </Typography>
              <ArrowForwardIcon sx={{ 
                fontSize: '1.1rem',
                color: color.evergreen,
                ml: 1
              }} />
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                pl: r(36),
                color: color.inkSoft,
                pr: r(36),
                lineHeight: 1.4
              }}
            >
              {AI_MENU_ITEM_DESCRIPTION}
            </Typography>
          </Box>
        </MenuItem>

        <MenuItem 
          onClick={() => {
            navigate(ANALYZE_ROUTE);
            setAnchorEl(null);
          }}
          sx={{
            py: 2,
            whiteSpace: 'normal',
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1,
            width: '100%'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1,
              width: '100%'
            }}>
              <ListItemIcon sx={{
                color: color.evergreen,
                minWidth: r(36)
              }}>
                <ListAltIcon />
              </ListItemIcon>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600,
                  color: color.ink,
                  flex: 1
                }}
              >
                {MENU_ITEM_TITLE}
              </Typography>
              <ArrowForwardIcon sx={{ 
                fontSize: '1.1rem',
                color: color.inkMuted,
                ml: 1
              }} />
            </Box>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                pl: r(36),
                pr: r(36),
                lineHeight: 1.4
              }}
            >
              {MENU_ITEM_DESCRIPTION}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}

export default CreateStackButton;